import React, { useState, useEffect } from 'react';
import { App, Table, Tag, Input, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { QUOTE_STATUS_COLORS, getQuoteStatusLabel } from '../utils/quoteUtils';
import { formatCurrency, formatDate } from '../utils/format';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface Quote {
  id: string;
  quote_number?: string;
  status: string;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  draft_order?: {
    display_id: number;
    total: number;
    currency_code: string;
  };
  service_provider?: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

const Quotes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(false);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadQuotes();
  }, [page, pageSize, statusFilter]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const params: any = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await apiClient.get('/admin/quotes', { params });
      setQuotes(response.data.quotes || []);
      setTotal(response.data.count || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('quotes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = quotes.filter((q) => {
    if (statusFilter !== 'all' && q.status !== statusFilter) {
      return false;
    }
    if (!debouncedSearch) return true;
    const customerName = q.customer
      ? `${q.customer.first_name} ${q.customer.last_name}`
      : '';
    const spName = q.service_provider?.name || '';
    const displayId = String(q.quote_number ?? q.draft_order?.display_id ?? '');
    return (
      customerName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      spName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      displayId.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  });

  const columns = [
    {
      title: t('quotes.displayId'),
      key: 'display_id',
      render: (_: any, record: Quote) => record.quote_number || record.draft_order?.display_id || '-',
    },
    {
      title: t('quotes.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={QUOTE_STATUS_COLORS[status] || 'default'}>
          {getQuoteStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('quotes.customer'),
      key: 'customer',
      render: (_: any, record: Quote) => {
        const c = record.customer;
        if (!c) return '-';
        return `${c.first_name} ${c.last_name}`;
      },
    },
    {
      title: t('quotes.serviceProvider'),
      key: 'service_provider',
      render: (_: any, record: Quote) => {
        if (!record.service_provider) return '-';
        return record.service_provider.name;
      },
    },
    {
      title: t('quotes.total'),
      key: 'total',
      render: (_: any, record: Quote) => {
        const total = record.draft_order?.total;
        if (total === undefined || total === null) return '-';
        return formatCurrency(total, record.draft_order?.currency_code, i18n.language);
      },
    },
    {
      title: t('common.updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  const tabItems = [
    { key: 'all', label: t('quotes.all') },
    { key: 'pending_service_provider', label: t('quotes.statusPendingSP') },
    { key: 'pending_customer', label: t('quotes.statusPendingCustomer') },
    { key: 'accepted', label: t('quotes.statusAccepted') },
    { key: 'customer_rejected', label: t('quotes.statusCustomerRejected') },
    { key: 'service_provider_rejected', label: t('quotes.statusSPRejected') },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('quotes.title')}
        </h1>
        <Input
          placeholder={t('common.search')}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Tabs
        activeKey={statusFilter}
        onChange={(key) => {
          setStatusFilter(key);
          setPage(1);
        }}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/quotes/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default Quotes;
